import { mat4 } from 'gl-matrix';
import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import { ImageBinding, placementKey } from '~/domain/patient/imageBinding';
import type { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { mat3, ReadonlyVec3, vec2, vec3 } from 'gl-matrix';
import { toPointMm } from '~/domain/geometry/patientGeometry';
import { isImagePlusSct, sctDensityAtHu } from '~/domain/image/sctDensityCurve';
import {
    carriesDensityOverride,
    densityOverrideAtPoint,
} from '~/domain/structures/densityOverrideAtPoint';
import { structureSdfOf } from '~/domain/structures/structureSdf';
import type { PublishedFrame } from '~/domain/viewport/publishedFrame';
import type { Environment } from '~/engine/data/Environment';
import type { DatasetEnvironmentRegistry } from '~/engine/scene/DatasetEnvironmentRegistry';
import { isSelectedStructurePeeked } from '~/engine/view/layers/contourAlpha';
import { viewDrawsDose } from '~/engine/view/layers/DoseLayer';
import {
    FusedImageDisplay,
    fusedPairOf,
    ViewFusion,
} from '~/engine/view/layers/fusedImageDisplay';
import { paneImageOf, ShownPaneImage } from '~/engine/view/paneImage';
import type { StructureProjection } from '~/engine/view/structureProjection';
import { CURVE_PICK_RADIUS_MM } from '~/engine/view/picking/curveSelectionBox';
import { ACQUIRED_SLICE_AXIS } from '~/engine/view/planeViewGeometry';
import { angleToNearestAxisDeg, sliceTiltDeg } from '~/utils/obliquity';
import type { ViewManager } from '~/engine/view/ViewManager';
import {
    Orientations,
    PlaneView,
    toViewId,
} from '~/engine/view/views/PlaneView';
import { downloadViewportScreenshot } from '~/hooks/viewer/screenshot';
import { computeDirectionLabels, directionLabel } from '~/utils/orientation';
import { planePositionAlong } from '~/utils/units';
import { ImageLayer, Plane, ViewportId } from '~/viewportGrid/layout/types';
import { ChangeNotifier } from '~/viewportGrid/adapters/changeSource';
import { cursorValueFor } from '~/viewportGrid/adapters/cursorReadout';
import {
    ChangeSource,
    CursorReadout,
    CursorSource,
    DensityCursorValue,
    DoseCursorValue,
    EdgeDirectionLabels,
    HoveredRoi,
    ImageCursorReadout,
    ImageSampling,
    MountParams,
    ObliqueCause,
    PlanePosition,
    RenderAdapter,
    SliceInfo,
    TileAlignment,
    TileFusion,
    TileGeometryLink,
    TileScreenshotOptions,
} from '~/viewportGrid/adapters/types';

/**
 * The registration `binding` names between the anchor image and a locked
 * row's image, or undefined for identity or a registration gone.
 */
export type AlignmentRegistrationResolver = (
    anchorSeriesUid: SeriesInstanceUid,
    alignedSeriesUid: SeriesInstanceUid,
    binding: ImageBinding
) => RigidRegistration | undefined;

/** The grid's per-tile layer choice, in the view's own vocabulary. */
const FUSED_DISPLAY_FOR_LAYER: Record<ImageLayer, FusedImageDisplay> = {
    [ImageLayer.Blended]: FusedImageDisplay.Blended,
    [ImageLayer.Primary]: FusedImageDisplay.PrimaryOnly,
    [ImageLayer.Secondary]: FusedImageDisplay.SecondaryOnly,
};

/** No partner held. */
const NO_PARTNER = { identity: '', key: undefined };

/** What tells two partners apart; empty for none. */
function partnerIdentity(partner: TileFusion['partner']): string {
    return partner
        ? placementKey(partner.seriesInstanceUid, partner.binding)
        : '';
}

/** Grid plane -> engine anatomical orientation. */
const ORIENTATION_FOR_PLANE: Record<Plane, Orientations> = {
    [Plane.Transversal]: Orientations.TRANSVERSAL_ORIENTATION,
    [Plane.Sagittal]: Orientations.SAGITTAL_ORIENTATION,
    [Plane.Coronal]: Orientations.CORONAL_ORIENTATION,
};

/**
 * Why a plane of `view` crosses the slices of the image `pane` shows: the
 * image's own grid lies off the patient axes, the registration that places
 * it on the pane turns it off them, the row's frame is turned, or more
 * than one of these.
 */
function obliqueCauseOf(pane: ShownPaneImage, view: PlaneView): ObliqueCause {
    const ownGrid = pane.image.parent.grid;
    return {
        acquisitionTiltDeg: sliceTiltDeg(ownGrid),
        // A registration that only moves the image, or turns it from one
        // patient axis onto another, leaves it on its slices.
        registrationTilts:
            !pane.grid.isAxisAligned() &&
            !mat3.equals(pane.grid.getOrientation(), ownGrid.getOrientation()),
        viewTurned: !view.isPatientAligned(),
    };
}

/**
 * How the image `pane` shows is sampled across its slices: the spacing of
 * its grid along the acquired slice axis, and the slice thickness its
 * DICOM states, when it states a usable one.
 */
function samplingOf(pane: ShownPaneImage): ImageSampling {
    const thickness = pane.image.parent.firstSlice?.sliceThickness;
    return {
        sliceSpacingMm: pane.grid.resZ,
        sliceThicknessMm:
            thickness !== undefined &&
            Number.isFinite(thickness) &&
            thickness > 0
                ? thickness
                : undefined,
    };
}

/** rAF ticks to wait for the tile canvas to acquire a non-zero layout size. */
const MAX_FIRST_FRAME_ATTEMPTS = 60;

/**
 * The engine view behind a tile's renderer, or undefined for a non-engine
 * renderer (the mock adapters) and before mount. The fence-legal downcast
 * every engine-backed tile overlay makes.
 */
export function planeViewOf(renderer: RenderAdapter): PlaneView | undefined {
    return renderer instanceof PlaneViewRenderAdapter
        ? renderer.planeView
        : undefined;
}

const NO_FRAMES = new ChangeNotifier();

/**
 * The {@link PlaneViewRenderAdapter.getFrameGeometrySource} of an engine
 * renderer; for any other renderer (the mock adapters), one that never
 * notifies.
 */
export function frameGeometrySourceOf(renderer: RenderAdapter): ChangeSource {
    return renderer instanceof PlaneViewRenderAdapter
        ? renderer.getFrameGeometrySource()
        : NO_FRAMES;
}

/**
 * Engine-backed {@link RenderAdapter} for ONE grid tile: one {@link PlaneView}
 * of a dataset's shared {@link Environment}, resolved through the
 * {@link DatasetEnvironmentRegistry}. This is the ACL — the only render seam
 * that touches the WebGL engine; the grid drives it through the port alone.
 *
 * The domain owns slice index / window-level / spacing and pushes them in; the
 * renderer only executes. The shared Environment (image texture + SDF) is
 * refcounted by the registry, so two tiles of the same series never diverge.
 */
export class PlaneViewRenderAdapter implements RenderAdapter {
    private view: PlaneView | undefined;
    private imageLayer: ImageLayer = ImageLayer.Blended;
    private environment: Environment | undefined;
    // The series whose Environment the view belongs to: the tile's own, or
    // the anchor's for a tile of a locked row.
    private seriesUid: SeriesInstanceUid | undefined;
    // The tile's own image as its anchor's Environment holds it, on a tile
    // of a locked row.
    private alignedImageKey: string | undefined;
    // The fusion the tile is asked to draw, kept across remounts, and the
    // partner the Environment holds for it while mounted.
    private fusion: TileFusion | undefined;
    private heldPartner: { identity: string; key: string | undefined } =
        NO_PARTNER;
    // Kept across remounts, as the fusion is.
    private geometryLink: TileGeometryLink | undefined;
    private plane: Plane | undefined;
    // A view's frame id restarts at zero on each mount, while this adapter
    // outlives a re-mount of its tile. The offset keeps every published id
    // this adapter reports unique across mounts, so a confirmation bound to
    // one id can never be honoured against a view mounted later.
    private frameIdOffset = 0;
    private highestFrameId = 0;

    /** The mounted engine view — for the engine overlays of a tile
     *  only; this never crosses the grid module fence. */
    public get planeView(): PlaneView | undefined {
        return this.view;
    }

    // The intersection-point Observable notifies with the SAME vec3 mutated
    // in place, so subscribers cannot diff snapshots — a version counter is
    // the change signal instead.
    private cursorObserver: ((point: ReadonlyVec3) => void) | undefined;
    private readonly cursorNotifier = new ChangeNotifier({
        onFirstSubscribe: () => this.attachCursorObserver(),
        onLastUnsubscribe: () => this.detachCursorObserver(),
    });

    // Apart from the cursor source: the scale also follows ViewManager-wide
    // zoom events, which would otherwise re-render every cursor readout.
    private detachDisplayScale: (() => void) | undefined;
    private displayScaleNotifyPending = false;
    private readonly displayScaleNotifier = new ChangeNotifier({
        onFirstSubscribe: () => this.attachDisplayScaleObservers(),
        onLastUnsubscribe: () => this.detachDisplayScaleObservers(),
    });

    private detachFrame: (() => void) | undefined;
    private readonly frameNotifier = new ChangeNotifier({
        onFirstSubscribe: () => this.attachFrameObserver(),
        onLastUnsubscribe: () => this.detachFrameObserver(),
    });

    public constructor(
        private readonly viewManager: ViewManager,
        private readonly registry: DatasetEnvironmentRegistry,
        private readonly tileId: ViewportId,
        private readonly registrationOf: AlignmentRegistrationResolver = () =>
            undefined
    ) {}

    public mount(params: MountParams): void {
        const ownSeriesUid = params.volume.metadata.seriesInstanceUid;
        const seriesUid = params.alignment?.anchorSeriesUid ?? ownSeriesUid;
        const handle = this.registry.acquire(seriesUid);
        const env = handle.environment;
        const image = env.image;
        if (!image) {
            // The factory failed to upload an image; do not leak the refcount.
            this.registry.release(seriesUid);
            throw new Error(
                'Acquired Environment has no image; cannot mount a plane view.'
            );
        }
        const alignedImageKey = params.alignment
            ? this.alignOnto(env, ownSeriesUid, params.alignment)
            : undefined;
        if (params.alignment && alignedImageKey === undefined) {
            // Never show the image unplaced in place of the placement asked for.
            this.registry.release(seriesUid);
            throw new Error(
                'The image cannot be placed on the image its row is locked to.'
            );
        }

        // Every hold taken above and below is released if the view cannot
        // join, so a failed mount leaves nothing uploaded in the anchor.
        let view: PlaneView | undefined;
        let joined = false;
        try {
            const orientationLabel = ORIENTATION_FOR_PLANE[params.plane];
            const orientation = PlaneView.computeViewOrientation(
                image.grid,
                orientationLabel
            );

            // The tile's 2D overlay carries the engine's tool cursor feedback
            // (rubber-bands, deform / box-zoom cursors). It is a real DOM sibling
            // layered over the WebGL canvas, so the feedback is visible; a detached
            // canvas would draw it into nowhere.
            view = new PlaneView(
                env,
                this.viewManager,
                orientation,
                params.canvas,
                params.overlayCanvas,
                toViewId(`grid:${this.tileId}`),
                orientationLabel
            );

            // Set before the view joins, so every listener of the joining view
            // sees the side it draws, not the blend.
            view.fusedImageDisplay = FUSED_DISPLAY_FOR_LAYER[this.imageLayer];
            view.alignedImageKey = alignedImageKey;
            // A locked tile shows its own image alone, so it draws no fusion.
            this.heldPartner = alignedImageKey
                ? NO_PARTNER
                : this.holdPartner(env, ownSeriesUid, this.fusion?.partner);
            view.viewFusion = alignedImageKey
                ? undefined
                : this.viewFusionOf(this.fusion);
            view.structureProjection =
                !params.alignment && params.structuresThrough
                    ? this.projectionOnto(
                          ownSeriesUid,
                          params.structuresThrough
                      )
                    : undefined;
            this.viewManager.addGridView(view);
            joined = true;
            this.linkGeometry(view);
        } catch (error) {
            // A view the manager never took still listens to the Environment.
            if (joined && view) {
                this.viewManager.removeGridView(view);
            } else {
                view?.delete();
            }
            if (alignedImageKey !== undefined) {
                env.releaseAlignedImage(alignedImageKey);
            }
            if (this.heldPartner.key !== undefined) {
                env.releaseAlignedImage(this.heldPartner.key);
            }
            this.heldPartner = NO_PARTNER;
            this.registry.release(seriesUid);
            throw error;
        }
        this.view = view;
        this.frameIdOffset = this.highestFrameId + 1;
        this.environment = env;
        this.seriesUid = seriesUid;
        this.alignedImageKey = alignedImageKey;
        this.plane = params.plane;
        this.attachCursorObserver();
        this.attachDisplayScaleObservers();
        this.attachFrameObserver();
        this.cursorNotifier.notify();

        // Paint the first frame once the canvas has a real layout size. The tile
        // may mount before layout settles (e.g. a route transition), and there
        // is no rAF pump, so a 0-size frame would render nothing and never
        // repaint. Wait for a non-zero size, then size the view and fit it
        // (the manager fits this view alone once its Environment is framed,
        // so it never moves another tile).
        this.paintFirstFrame(view, 0);
    }

    public setGeometryLink(link: TileGeometryLink | undefined): void {
        this.geometryLink = link;
        if (this.view) {
            this.linkGeometry(this.view);
        }
    }

    private linkGeometry(view: PlaneView): void {
        if (this.geometryLink) {
            view.environment.geometryLink.setRole(view, this.geometryLink);
        } else {
            view.geometryLink?.setRole(view, undefined);
        }
    }

    public setFusion(fusion: TileFusion | undefined): void {
        this.fusion = fusion;
        const { view, environment, seriesUid } = this;
        if (
            !view ||
            !environment ||
            !seriesUid ||
            this.alignedImageKey !== undefined
        ) {
            return;
        }
        if (partnerIdentity(fusion?.partner) !== this.heldPartner.identity) {
            // The new partner is held before the old is let go, so a
            // volume both share is not freed and uploaded again.
            const previous = this.heldPartner.key;
            this.heldPartner = this.holdPartner(
                environment,
                seriesUid,
                fusion?.partner
            );
            if (previous !== undefined) {
                environment.releaseAlignedImage(previous);
            }
        }
        view.viewFusion = this.viewFusionOf(fusion);
    }

    /**
     * Holds `partner` in `env`, placed on the image `ownSeriesUid` through
     * its binding. The key is undefined when the binding no longer places
     * it: the tile then fuses nothing rather than an unplaced image.
     */
    private holdPartner(
        env: Environment,
        ownSeriesUid: SeriesInstanceUid,
        partner: TileFusion['partner']
    ): { identity: string; key: string | undefined } {
        return {
            identity: partnerIdentity(partner),
            key: partner
                ? this.alignOnto(env, partner.seriesInstanceUid, {
                      anchorSeriesUid: ownSeriesUid,
                      binding: partner.binding,
                  })
                : undefined,
        };
    }

    /** The engine's view of `fusion`, through the partner held for it. */
    private viewFusionOf(
        fusion: TileFusion | undefined
    ): ViewFusion | undefined {
        return (
            fusion && {
                alignedImageKey: this.heldPartner.key,
                blend: fusion.blend,
            }
        );
    }

    /**
     * Holds the image `alignedSeriesUid` in the anchor's Environment `env`,
     * placed through the alignment's binding; the key it is held by, or
     * undefined when the binding no longer places it.
     */
    private alignOnto(
        env: Environment,
        alignedSeriesUid: SeriesInstanceUid,
        alignment: TileAlignment
    ): string | undefined {
        const entry = this.registry.entryFor(alignedSeriesUid);
        if (!entry) {
            return undefined;
        }
        if (alignment.binding.kind === 'identity') {
            return env.retainAlignedImage(entry.image);
        }
        const registration = this.registrationOf(
            alignment.anchorSeriesUid,
            alignedSeriesUid,
            alignment.binding
        );
        return registration
            ? env.retainAlignedImage(entry.image, registration)
            : undefined;
    }

    /**
     * The anchor's structure set as a view of `ownSeriesUid` draws it: the
     * set the anchor's resident Environment holds, placed through the
     * binding into the own image's frame. Both are read on every draw, so
     * an Environment minted again or a registration edited since shows as
     * it now is. Undefined when the binding's registration is gone.
     */
    private projectionOnto(
        ownSeriesUid: SeriesInstanceUid,
        through: TileAlignment
    ): StructureProjection | undefined {
        const anchorUid = through.anchorSeriesUid;
        const anchor = this.registry.entryFor(anchorUid)?.image;
        const own = this.registry.entryFor(ownSeriesUid)?.image;
        if (!anchor || !own) {
            return undefined;
        }
        const registration =
            through.binding.kind === 'mapped'
                ? this.registrationOf(anchorUid, ownSeriesUid, through.binding)
                : undefined;
        if (through.binding.kind === 'mapped' && !registration) {
            return undefined;
        }
        const identity = mat4.create();
        return {
            structureSet: () =>
                this.registry.environmentFor(anchorUid)?.structureSet,
            placement: () =>
                registration
                    ? registration.getTransformFromTo(
                          anchor.frameOfReferenceUid,
                          own.frameOfReferenceUid
                      )
                    : identity,
        };
    }

    private paintFirstFrame(view: PlaneView, attempt: number): void {
        // Stop if this renderer was disposed or re-mounted onto a new view.
        if (this.view !== view) {
            return;
        }
        const size = view.size;
        if (size[0] > 0 && size[1] > 0) {
            view.resize(size);
            this.viewManager.fitGridView(view);
            // The fit moves this view's plane, which the cursor readers
            // read with the cursor; the shared cursor itself may not move.
            this.cursorNotifier.notify();
            return;
        }
        if (attempt < MAX_FIRST_FRAME_ATTEMPTS) {
            requestAnimationFrame(() =>
                this.paintFirstFrame(view, attempt + 1)
            );
        }
    }

    private attachCursorObserver(): void {
        const env = this.environment;
        if (!env || this.cursorObserver || !this.cursorNotifier.hasListeners) {
            return;
        }
        this.cursorObserver = () => this.cursorNotifier.notify();
        env.planeIntersectionPoint.subscribe(this.cursorObserver);
        // A registration write moves a fused secondary under a still cursor,
        // which changes what a pane showing it reads.
        this.viewManager.addEventListener(
            'registration_changed',
            this.notifyCursor
        );
        // A dose set, removed, shown or hidden changes the dose a pane
        // reads under a still cursor.
        this.viewManager.addEventListener(
            'dose_display_changed',
            this.notifyCursor
        );
    }

    private detachCursorObserver(): void {
        if (this.environment && this.cursorObserver) {
            this.environment.planeIntersectionPoint.unsubscribe(
                this.cursorObserver
            );
            this.viewManager.removeEventListener(
                'registration_changed',
                this.notifyCursor
            );
            this.viewManager.removeEventListener(
                'dose_display_changed',
                this.notifyCursor
            );
        }
        this.cursorObserver = undefined;
    }

    private readonly notifyCursor = (): void => this.cursorNotifier.notify();

    /**
     * Follows the display scale of the mounted view while anyone listens:
     * a zoom or a reset fit, which the ViewManager announces as
     * `zoomScaleChanged` without naming the view; a focus, which fits the
     * views of this tile's Environment and moves that Environment's shared
     * point; and a resize of the canvas. The resize observer is created
     * after the view's own, so it is called after the engine has refitted
     * the view to its new size.
     */
    private attachDisplayScaleObservers(): void {
        const canvas = this.view?.canvas;
        const env = this.environment;
        if (
            !canvas ||
            !env ||
            this.detachDisplayScale ||
            !this.displayScaleNotifier.hasListeners
        ) {
            return;
        }
        const schedule = () => this.scheduleDisplayScaleNotify();
        this.viewManager.addEventListener('zoomScaleChanged', schedule);
        env.planeIntersectionPoint.subscribe(schedule);
        const resizeObserver =
            typeof ResizeObserver === 'undefined'
                ? undefined
                : new ResizeObserver(schedule);
        resizeObserver?.observe(canvas);
        this.detachDisplayScale = () => {
            this.viewManager.removeEventListener('zoomScaleChanged', schedule);
            env.planeIntersectionPoint.unsubscribe(schedule);
            resizeObserver?.disconnect();
        };
    }

    private detachDisplayScaleObservers(): void {
        this.detachDisplayScale?.();
        this.detachDisplayScale = undefined;
    }

    /**
     * Notifies once the engine call that announced the change has returned:
     * a focus moves the shared point after its first view's fit and before
     * the fit of the others, so a read inside the event would see
     * those views at their old scale. Changes in one call notify once.
     */
    private scheduleDisplayScaleNotify(): void {
        if (this.displayScaleNotifyPending) {
            return;
        }
        this.displayScaleNotifyPending = true;
        queueMicrotask(() => {
            this.displayScaleNotifyPending = false;
            this.displayScaleNotifier.notify();
        });
    }

    private attachFrameObserver(): void {
        const view = this.view;
        if (!view || this.detachFrame || !this.frameNotifier.hasListeners) {
            return;
        }
        let shown = view.canvasFrameGeometry;
        const observer = () => {
            if (view.canvasFrameGeometry !== shown) {
                shown = view.canvasFrameGeometry;
                this.frameNotifier.notify();
            }
        };
        view.canvasFramePublished.subscribe(observer);
        this.detachFrame = () =>
            view.canvasFramePublished.unsubscribe(observer);
    }

    private detachFrameObserver(): void {
        this.detachFrame?.();
        this.detachFrame = undefined;
    }

    /**
     * The signed offset of the tile's plane along its normal, in mm, read
     * along the patient axis the normal lies nearest to: the plane's
     * coordinate on that axis for a patient-aligned view. The same for
     * every point of the displayed plane, an oblique one included. Read in
     * the frame of the image the tile shows, so a secondary drawn alone
     * reports its own position, on the axis its own frame names.
     */
    public getPlanePosition(): PlanePosition | undefined {
        const view = this.planeView;
        if (!view) {
            return undefined;
        }
        const pane = paneImageOf(view);
        if (pane.kind === 'none') {
            return undefined;
        }
        const pointMm = toPointMm(view.planeIntersectionPoint.coordinates);
        const point = pane.toImageFrame(pointMm);
        const normal = vec3.subtract(
            vec3.create(),
            pane.toImageFrame(
                toPointMm(vec3.add(vec3.create(), pointMm, view.viewDirection))
            ),
            point
        );
        return planePositionAlong(point, normal);
    }

    public getCursorSource(): CursorSource {
        return this.cursorNotifier;
    }

    /**
     * Which of the row's fused pair this tile draws. Applied to the view, so
     * two tiles of one row can show opposite sides of the same fusion.
     */
    public setImageLayer(layer: ImageLayer): void {
        this.imageLayer = layer;
        if (this.view) {
            this.view.fusedImageDisplay = FUSED_DISPLAY_FOR_LAYER[layer];
        }
    }

    /**
     * The value of the image the tile draws under a client point, so a
     * pane showing the secondary alone never reports a value sampled from
     * the primary under it. A blended tile also reads the fused secondary
     * at the same point, and a tile that draws a dose reads the dose there,
     * and the density in force there ({@link densityAt}).
     * The point is read in the shown image's own frame. Off the shown image
     * there is no image value, never a faked one: the readout is the dose
     * the tile draws at the point, or nothing where it draws none.
     */
    public readCursor(
        clientX: number,
        clientY: number
    ): CursorReadout | undefined {
        const view = this.planeView;
        if (!view || clientX < 0 || clientY < 0) {
            return undefined;
        }
        const pane = paneImageOf(view);
        if (pane.kind === 'none') {
            return undefined;
        }
        const mm = view.clientToPatientCoordinates(
            vec2.fromValues(clientX, clientY)
        );
        const dose = this.doseDrawnAt(view, mm);
        const sampled = pane.image.getImageObjectValueByCoordinate(mm);
        if (sampled === null) {
            return dose && { dose };
        }
        const fused =
            view.fusedImageDisplay === FusedImageDisplay.Blended
                ? fusedPairOf(view).secondaryImage
                : undefined;
        const patientMm = pane.toImageFrame(toPointMm(mm));
        const density = this.densityAt(view, pane, mm, sampled);
        const readout: ImageCursorReadout = {
            value: cursorValueFor(pane.image.parent, sampled),
            patientMm,
            ...(dose && { dose }),
            ...(density && { density }),
        };
        if (!fused) {
            return readout;
        }
        const fusedSample = fused.getImageObjectValueByCoordinate(mm);
        return {
            ...readout,
            fusedValue:
                fusedSample === null
                    ? null
                    : cursorValueFor(fused.parent, fusedSample),
        };
    }

    /**
     * The dose `view` draws at a point of its pane (mm), as the dose stores
     * it, or undefined where the view draws none: it does not draw the dose
     * ({@link viewDrawsDose}), the dose is hidden, or the point lies
     * outside the dose grid.
     */
    private doseDrawnAt(
        view: PlaneView,
        paneMm: ReadonlyVec3
    ): DoseCursorValue | undefined {
        const dose = view.environment.dose;
        if (!dose || !dose.isVisible || !viewDrawsDose(view)) {
            return undefined;
        }
        const value = dose.getDoseIntensityValueAtCursorCoordinate(paneMm);
        return value === null
            ? undefined
            : { value, unit: dose.parent.doseUnits, doseId: dose.parent.id };
    }

    /**
     * The density in force at a point of `pane` (mm) that samples `value`,
     * or undefined where none is. A density override is read only on a
     * pane that shows the primary, the image of the Environment's structure
     * set, and only while that set still has its image: the structure the
     * point lies in, innermost first. Where no override is in force, a pane
     * that shows an Image+ synthetic CT reads the density Image+ derives
     * from the value, and none outside the Image+ table.
     */
    private densityAt(
        view: PlaneView,
        pane: ShownPaneImage,
        paneMm: ReadonlyVec3,
        value: number
    ): DensityCursorValue | undefined {
        const structureSetObject =
            pane.kind === 'primary' ? view.environment.structureSet : undefined;
        const structureSet = structureSetObject?.parent;
        // Only structures that carry an override are read: reading a field
        // can be a full-volume readback, and `builtTexture` never
        // rasterises one on the pointer path.
        const override =
            structureSetObject && structureSet?.image
                ? densityOverrideAtPoint(
                      structureSet.roiArray
                          .filter(carriesDensityOverride)
                          .map((roi) => ({
                              name: roi.name,
                              electronDensityOverride:
                                  roi.electronDensityOverride,
                              massDensityOverride: roi.massDensityOverride,
                              sdf: structureSdfOf(
                                  structureSetObject.getRoi(roi.roiNumber)
                                      ?.builtTexture
                              ),
                          })),
                      paneMm
                  )
                : undefined;
        if (override) {
            return { kind: 'override', ...override };
        }
        const sct = isImagePlusSct(pane.image.parent)
            ? sctDensityAtHu(value)
            : undefined;
        return sct && { kind: 'sct', ...sct };
    }

    public readRoiAt(clientX: number, clientY: number): HoveredRoi | undefined {
        const view = this.view;
        const env = this.environment;
        if (!view || !env || clientX < 0 || clientY < 0) {
            return undefined;
        }
        // Within the curve pick radius, in millimetres: the distance at which
        // a structure is named is the distance at which the deletion offered
        // on it picks a curve, the same around every structure. Read-only.
        const structureSet = env.structureSet;
        // A peeked structure is passed over: a label beside a contour the
        // view is not drawing would point at nothing, and a drawn one near
        // it still has its name.
        const selected = structureSet?.selectedStructure;
        const peeked =
            selected &&
            isSelectedStructurePeeked(selected.roiNumber, env.peekedRoiNumber)
                ? selected
                : undefined;
        const roi = structureSet?.findRoiWithinMm(
            view.clientToPatientCoordinates(vec2.fromValues(clientX, clientY)),
            CURVE_PICK_RADIUS_MM,
            peeked
        );
        // The ROI Number names the structure only within the set this
        // Environment draws; the id names it anywhere.
        const roiId = roi && structureSet?.parent.roiIdOf(roi.roiNumber);
        return roi && roiId ? { name: roi.name, roiId } : undefined;
    }

    public consumeClickSuppression(): boolean {
        // The pan action arms the flag on the view it dragged.
        return this.view?.consumeClickSuppression() ?? false;
    }

    public getEdgeLabels(): EdgeDirectionLabels | undefined {
        const view = this.view;
        if (!view) {
            return undefined;
        }
        // The letters come from the view's real orientation
        // (getOrientation() is the computeViewOrientation output), so a
        // flipped/oblique view relabels R/L instead of showing the
        // canonical letters.
        return computeDirectionLabels(view.getOrientation());
    }

    /**
     * Where the tile's plane sits in the image the tile draws, read from
     * that image's grid on the pane: a secondary drawn alone counts its own
     * slices, and is oblique where the registration tilts it off them. The
     * slice is the grid voxel the shared point lies in, the one rule a
     * sampled value follows.
     */
    public getSliceInfo(): SliceInfo | undefined {
        const view = this.planeView;
        if (!view) {
            return undefined;
        }
        const pane = paneImageOf(view);
        if (pane.kind === 'none') {
            return undefined;
        }
        const grid = pane.grid;
        // The grid's x and y resolution are DICOM PixelSpacing [row, column].
        const pixelSpacingMm = { row: grid.resX, column: grid.resY };
        const sampling = samplingOf(pane);
        const axis = grid.axisAlong(view.viewDirection);
        if (axis === undefined) {
            return {
                kind: 'oblique',
                angleDeg: angleToNearestAxisDeg(view.viewDirection, [
                    grid.getUnitX(),
                    grid.getUnitY(),
                    grid.getUnitZ(),
                ]),
                cause: obliqueCauseOf(pane, view),
                pixelSpacingMm,
                sampling,
            };
        }
        const gridAxes = [grid.getUnitX(), grid.getUnitY(), grid.getUnitZ()];
        const count = grid.getSize()[axis];
        const index = Math.floor(
            grid.toVoxelSpace(view.planeIntersectionPoint.coordinates)[axis]
        );
        const inImage = index >= 0 && index < count;
        return {
            kind: 'slice',
            index: inImage ? index : undefined,
            count,
            // The pane's grid is the image's own volume, moved by any
            // registration, so its slice index is the image's own.
            interpolated:
                inImage &&
                axis === ACQUIRED_SLICE_AXIS &&
                pane.image.parent.isSliceInterpolated(index),
            risesToward: directionLabel(gridAxes[axis]),
            pixelSpacingMm,
            sampling,
        };
    }

    public getMmPerCssPixel(): number | undefined {
        const view = this.view;
        if (!view) {
            return undefined;
        }
        // resolution[0] is patient mm per pixel at the current zoom, set by
        // fitToView on the first frame; guard until it is positive + finite.
        const mmPerPixel = view.resolution[0];
        return Number.isFinite(mmPerPixel) && mmPerPixel > 0
            ? mmPerPixel
            : undefined;
    }

    public getDisplayScaleSource(): ChangeSource {
        return this.displayScaleNotifier;
    }

    /**
     * Notifies, in the task that publishes the frame, each time the mounted
     * view's {@link PlaneView.canvasFrameGeometry} changes.
     */
    public getFrameGeometrySource(): ChangeSource {
        return this.frameNotifier;
    }

    public getPublishedFrame(): PublishedFrame | undefined {
        const view = this.view;
        // A view that draws no image publishes no frame: its canvas holds no
        // DICOM pixels, so there is nothing a capture could attest to.
        if (!view || paneImageOf(view).kind === 'none') {
            return undefined;
        }
        // A snapshot, not the view: the sources and id the engine committed
        // with the frame on the canvas, read together.
        const canvasFrameId = this.frameIdOffset + view.canvasFrameId;
        this.highestFrameId = Math.max(this.highestFrameId, canvasFrameId);
        return { canvasFrameSources: view.canvasFrameSources, canvasFrameId };
    }

    public captureScreenshot(options: TileScreenshotOptions): Promise<void> {
        const view = this.view;
        const plane = this.plane;
        if (!view || plane === undefined) {
            return Promise.reject(
                new Error(
                    'Cannot capture a screenshot before the tile is mounted.'
                )
            );
        }
        // The plane label names the tile; the view's own geometry and the
        // image it shows decide whether the file may carry it, so neither a
        // tilted view nor an image the registration tilts off its slices is
        // named after a plane it is not on.
        return downloadViewportScreenshot(view, {
            orientation: ORIENTATION_FOR_PLANE[plane],
            isOblique:
                !view.isPatientAligned() ||
                this.getSliceInfo()?.kind === 'oblique',
            // Only a preview drawn into this tile's image: an operation
            // previews in its Environment, and the couch model ghost sits on
            // the 2D overlay the capture leaves out.
            showsUnappliedPreview:
                view.environment.operationPreviewStructure !== undefined,
            // The selected ROI names the file only when this tile draws its
            // set.
            roiNumber:
                options.roiId === undefined
                    ? undefined
                    : view.environment.structureSet?.parent.getRoiById(
                          options.roiId
                      )?.roiNumber,
            capturedAt: options.capturedAt,
        });
    }

    public dispose(): void {
        this.detachCursorObserver();
        this.detachDisplayScaleObservers();
        this.detachFrameObserver();
        if (this.view) {
            // Frees this PlaneView's FBO + layers and unsubscribes its
            // observers; never touches the shared image texture.
            this.viewManager.removeGridView(this.view);
        }
        if (this.alignedImageKey !== undefined) {
            this.environment?.releaseAlignedImage(this.alignedImageKey);
        }
        if (this.heldPartner.key !== undefined) {
            this.environment?.releaseAlignedImage(this.heldPartner.key);
        }
        this.heldPartner = NO_PARTNER;
        if (this.seriesUid) {
            // Drops the shared Environment refcount; textures free at zero.
            this.registry.release(this.seriesUid);
        }
        this.view = undefined;
        this.environment = undefined;
        this.seriesUid = undefined;
        this.alignedImageKey = undefined;
        this.plane = undefined;
    }
}
